import {
    afterNextRender,
    Injector,
    viewChild,
    ChangeDetectionStrategy,
    Component,
    computed,
    effect,
    inject,
    input,
    signal,
    untracked,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { NgIcon } from '@ng-icons/core';
import type { PackActionProposal } from '@quipex/shared/contracts';
import { QpxButton, QpxIcon, QpxPill, QpxTypography } from '@quipex/shared/ui';
import {
    QUIPPY_SESSION,
    type QuippyAssistantAppearance,
} from '../../../runtime';
import { buildPackCommand } from '../../../runtime/pack-actions/command';
import { QuippyPackActions } from '../../../runtime/pack-actions/pack-actions';
import { DescriptionDisclosure } from './description-disclosure';
import { FocusRestoration } from './focus-restoration';
import { toDraftCommandSections } from './mapper';
import { QuippyPackDestination } from './pack-destination/pack-destination';
import { DocumentDragState } from './pack-document/document-drag-state';
import { QuippyPackEditor } from './pack-editor/pack-editor';
import { QuippyPackResult } from './pack-result/pack-result';
import { ProposalState } from './proposal-state';

@Component({
    selector: 'qpx-quippy-pack-action',
    imports: [
        QuippyPackDestination,
        QuippyPackEditor,
        NgIcon,
        QpxIcon,
        QpxTypography,
        QuippyPackResult,
        QpxButton,
        MatButtonModule,
    ],
    templateUrl: './pack-action.html',
    styleUrl: './pack-action.scss',
    providers: [
        ProposalState,
        FocusRestoration,
        DocumentDragState,
        DescriptionDisclosure,
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: {
        class: 'block',
        '[class.shadow-xs]': "appearance() === 'elevated'",
        '[class.outlined]': "appearance() === 'outlined'",
        role: 'group',
        'aria-label': 'Pack proposal',
    },
})
export class QuippyPackAction {
    private readonly descriptionDisclosure = inject(DescriptionDisclosure);
    private readonly injector = inject(Injector);
    private readonly session = inject(QUIPPY_SESSION, { optional: true });
    private readonly destination = viewChild(QuippyPackDestination);
    protected readonly actions = inject(QuippyPackActions);
    public readonly appearance = input<QuippyAssistantAppearance>('elevated');
    public readonly actionId = input.required<string>();
    public readonly proposal = input<PackActionProposal>();
    protected readonly state = computed(() =>
        this.actions.state(this.actionId())
    );
    protected readonly draft = inject(ProposalState);
    protected readonly error = signal('');
    protected readonly busy = computed(
        () => this.state()?.phase === 'applying'
    );
    protected readonly canSelect = computed(
        () =>
            !['applying', 'uncertain', 'applied'].includes(
                this.state()?.phase ?? ''
            )
    );

    constructor() {
        effect(() => {
            const proposal = this.proposal();
            if (!proposal) return;
            const id = this.actionId();
            untracked(() => {
                this.actions.proposal(id, proposal);
                this.draft.initialise(proposal);
                this.descriptionDisclosure.reset();
            });
        });
    }
    protected editDetails(): void {
        this.invalidate();
        afterNextRender(() => this.destination()?.focusName(), {
            injector: this.injector,
        });
    }
    protected invalidate(): void {
        const proposal = this.proposal();
        if (proposal) this.actions.edit(this.actionId(), proposal);
        this.error.set('');
    }
    protected async preview(): Promise<void> {
        const proposal = this.proposal();
        if (!proposal || !this.canSelect() || this.busy()) return;
        this.error.set('');
        const documents = this.draft
            .orderedDocuments()
            .filter((document) =>
                this.draft.selectedDocuments().has(document.documentId)
            );
        try {
            const command = buildPackCommand({
                proposal,
                documents,
                name: this.draft.name(),
                description: this.draft.description(),
                sections: toDraftCommandSections(this.draft.groups()),
            });
            this.actions.preview(
                this.actionId(),
                proposal,
                command,
                this.session?.buildingDetails({
                    kind: 'building',
                    buildingId: proposal.buildingId,
                })?.name ?? 'Selected building'
            );
        } catch (error) {
            this.error.set(
                error instanceof Error
                    ? error.message
                    : 'Check the pack structure and try again.'
            );
        }
    }
}
